/**
 * UK bank holidays, read from the gov.uk feed.
 *
 * They matter twice on the date step: the calendar's bulk-booking toggle skips
 * them, and — since a bank holiday suspends a site's open window — a shift on
 * one is charged where it would normally be free. The API reads the same feed
 * (App\Services\BankHolidayService) and applies the same rule, so the hours
 * shown here match the hours billed.
 *
 * England and Wales only, matching the API: Scotland and Northern Ireland keep
 * their own lists and VSOC charge against the England and Wales dates.
 */

import { IsoDateString, UsDateString, isoToUs } from './date';

interface BankHolidayEvent {
    title: string;
    date: string;
    notes: string;
    bunting: boolean;
}

interface Division {
    division: string;
    events: BankHolidayEvent[];
}

type BankHolidayFeed = Record<string, Division>;

export const BANK_HOLIDAYS_URL = 'https://www.gov.uk/bank-holidays.json';
export const BANK_HOLIDAY_DIVISION = 'england-and-wales';

/** ISO date → the holiday's name, e.g. "2026-08-31" → "Summer bank holiday". */
export type BankHolidayMap = Record<IsoDateString, string>;

/**
 * Shared across the three date-selection forms, which mount separately but ask
 * for the same list.
 */
let inFlight: Promise<BankHolidayMap> | null = null;

/**
 * Every England and Wales bank holiday the feed carries, keyed by ISO date.
 *
 * Deliberately not filtered to the current year: bookings run up to a year out,
 * so a December booking of the following May's bank holiday has to be seen.
 * Resolves to {} if the feed cannot be read — the API is the authority on the
 * charge either way.
 */
export const fetchBankHolidays = (): Promise<BankHolidayMap> => {
    if (!inFlight) {
        inFlight = fetch(BANK_HOLIDAYS_URL)
            .then((response) => {
                // fetch only rejects on a network failure, so a 500 or a 404
                // arrives here as a resolved response whose body is an error
                // page. Refuse it by status rather than waiting for the parse
                // to fail on whatever came back.
                if (!response.ok) {
                    throw new Error(`Failed to fetch bank holidays: ${response.status}`);
                }

                return response.json();
            })
            .then((feed: BankHolidayFeed) => {
                const events = feed?.[BANK_HOLIDAY_DIVISION]?.events ?? [];
                return events.reduce<BankHolidayMap>((acc, event) => {
                    if (event?.date) {
                        acc[event.date] = event.title;
                    }
                    return acc;
                }, {});
            })
            .catch((error) => {
                console.error('fetchBankHolidays failed', error);
                // Cleared so a later mount can try again rather than being stuck
                // with the empty result.
                inFlight = null;
                return {};
            });
    }

    return inFlight;
};

/** The calendar works in MM-DD-YYYY, so hand it the dates in its own format. */
export const toCalendarHolidays = (holidays: BankHolidayMap): UsDateString[] => (
    Object.keys(holidays).map(isoToUs)
);

/** Drop the titles — the billing helpers only need the dates. */
export const bankHolidayDates = (holidays: BankHolidayMap): IsoDateString[] => (
    Object.keys(holidays)
);
